import { ApolloQueryResult, useQuery } from "@apollo/client";
import { ErrorOutlineRounded } from "@mui/icons-material";
import {
  Box,
  Step,
  StepButton,
  StepContent,
  StepLabel,
  Stepper,
} from "@mui/material";
import { ReactElement, ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  CampaignStatus,
  GetCampaignQuery,
  GetCampaignQueryVariables,
  TrackedJob,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { GET_CAMPAIGN } from "~frontend/graphql/campaigns";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { Tenant, useTenant } from "~frontend/providers/LoggedInUserProvider";
import { SubaccountAdminData } from "~frontend/providers/LoggedInUserProvider/context";
import {
  useSubaccount,
  useSubaccountAdminConfig,
} from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { jobInProgress } from "~frontend/util/jobs";

import Basics, { validateBasics } from "./Basics";
import ContactMapping, { validateContactMapping } from "./ContactMapping";
import { InitialMessage, validateScripts } from "./InitialMessage";
import LoadContacts, { validateContacts } from "./LoadContacts";
import ResponseScripts from "./ResponseScripts";
import SendingNumbers, { validateSendingNumbers } from "./SendingNumbers";
import StartCampaign, { ValidationError } from "./StartCampaign";
import Suppressions from "./Suppressions";
import { suppressionJobKeys } from "./Suppressions/suppressionJobs";
import Tags from "./Tags";
import TopActionButtons from "./campaignEditorUtils/TopActionButtons";
import { getRunningOverlapJobForCampaign } from "./campaignEditorUtils/jobs";
import { CampaignForEditor } from "./types";

type ValidationState =
  | { loading: true; result: null }
  | {
      loading?: false;
      result: {
        errors: ValidationError[];
        sectionsWithErrors: Set<string>;
      };
    };

type StepDefinition = {
  name: string;
  slug: string;
  validator?: (props: {
    tenant: Tenant;
    subaccount: SubaccountAdminData;
    campaign: CampaignForEditor;
    subaccountLink: (relativePath: string) => string;
  }) =>
    | { title: string; message: ReactNode }[]
    | Promise<{ title: string; message: ReactNode }[]>;
  job?: Pick<TrackedJob, "status" | "progress"> | null;
  hidden?: boolean;
} & (
  | {
      // The "Start Campaign" step specifies runValidation: true, which means
      // we show validation errors in the sidebar and also pass those validation
      // errors down to the component
      runValidation: true;
      component: (props: {
        campaign: CampaignForEditor;
        goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
        validationErrors: ValidationError[];
        refreshCampaign: () => Promise<ApolloQueryResult<GetCampaignQuery>>;
      }) => ReactElement;
    }
  | {
      // Other steps don't specify runValidation, so we don't pass
      // validationErrors to the component
      runValidation?: false;
      component: (props: {
        campaign: CampaignForEditor;
        goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
        refreshCampaign: () => Promise<ApolloQueryResult<GetCampaignQuery>>;
      }) => ReactElement;
    }
);

const jobKeys = [
  "contactImportJob",
  "campaignStartJob",
  "responseScriptImportToCampaignJob",
  "contactCountJob",
  ...suppressionJobKeys,
] as const;

export function CampaignEditor(): ReactElement {
  const { subaccountLink } = useSubaccount()!;
  const subaccount = useSubaccountAdminConfig()!;
  const tenant = useTenant()!;
  const navigate = useNavigate();
  const { campaignShortId: maybeCampaignShortId, section } = useParams<{
    campaignShortId: string;
    section: string;
  }>();
  const campaignShortId = maybeCampaignShortId!;
  const { loading, error, data, refetch, startPolling, stopPolling } = useQuery<
    GetCampaignQuery,
    GetCampaignQueryVariables
  >(GET_CAMPAIGN, {
    variables: {
      input: {
        shortId: parseInt(campaignShortId),
      },
    },
  });

  useBreadcrumbs([
    {
      label: "Campaigns",
      path: subaccountLink("/campaigns"),
    },
    ...(data?.getCampaignByShortId
      ? [
          {
            label:
              data.getCampaignByShortId.status === CampaignStatus.Archived
                ? `${data.getCampaignByShortId.name} (Archived)`
                : data.getCampaignByShortId.name,
            path: subaccountLink(`/campaigns/${campaignShortId}/edit/basics`),
          },
        ]
      : []),
  ]);

  const anyJobRunning = jobKeys.some(
    jobKey =>
      data?.getCampaignByShortId[jobKey] &&
      jobInProgress(data.getCampaignByShortId[jobKey]!)
  );
  const sendInProgress =
    data?.getCampaignByShortId.status === CampaignStatus.InitialSendRunning ||
    data?.getCampaignByShortId.status === CampaignStatus.InitialSendCanceling;
  useEffect(() => {
    if (anyJobRunning) {
      startPolling(1000);
    } else if (sendInProgress) {
      startPolling(60000);
    }

    return () => stopPolling();
  }, [anyJobRunning, sendInProgress, startPolling, stopPolling]);

  const detailsSteps: StepDefinition[] = useMemo(
    () => [
      {
        name: "Basics",
        slug: "basics",
        component: Basics,
        validator: validateBasics,
      },
      {
        name: "Sending Numbers",
        slug: "sending-numbers",
        component: SendingNumbers,
        validator: validateSendingNumbers,
      },
      {
        name: "Load Contacts",
        slug: "contacts",
        component: LoadContacts,
        validator: validateContacts,
        job:
          data?.getCampaignByShortId.contactImportJob ||
          getRunningOverlapJobForCampaign(data?.getCampaignByShortId),
      },
      {
        name: "Contact Mapping",
        slug: "mapping",
        component: ContactMapping,
        validator: validateContactMapping,
      },
      {
        name: "Suppressions",
        slug: "suppressions",
        component: Suppressions,
      },
      {
        name: "Initial Message",
        slug: "script",
        component: InitialMessage,
        validator: validateScripts,
      },
      {
        name: "Response Scripts",
        slug: "responses",
        component: ResponseScripts,
      },
      {
        name: "Tags",
        slug: "tags",
        component: Tags,
      },
      {
        name: "Start Campaign",
        slug: "start",
        component: StartCampaign,
        runValidation: true,
        job: data?.getCampaignByShortId.campaignStartJob,
        hidden:
          data?.getCampaignByShortId.startedAt ||
          data?.getCampaignByShortId.status === CampaignStatus.Archived,
      },
    ],
    [data]
  );

  let activeStep = detailsSteps.findIndex(({ slug }) => slug === section);
  if (activeStep === -1) {
    activeStep = 0;
  }

  const goToStep = (
    step: number,
    {
      bypassRouteLeavingGuard = false,
    }: { bypassRouteLeavingGuard?: boolean } = {}
  ) => {
    navigate(
      subaccountLink(
        `/campaigns/${campaignShortId}/edit/${detailsSteps[step].slug}`
      ),
      {
        state: { bypassRouteLeavingGuard },
      }
    )?.catch(console.error);
  };

  const goToNextSection = ({
    bypassRouteLeavingGuard = false,
  }: { bypassRouteLeavingGuard?: boolean } = {}) => {
    // We have the bypassRouteLeavingGuard option because we want to be able to
    // navigate right after the user saves, but right after the user saves,
    // React hasn't re-rendered the Formik form yet with `dirty: false` so the
    // RouteLeavingGuard still things we have unsaved changes.
    if (activeStep < detailsSteps.length - 1) {
      goToStep(activeStep + 1, { bypassRouteLeavingGuard });
    }
  };

  const CurrentPage = detailsSteps[activeStep].component;

  const [validationState, setValidationState] = useState<ValidationState>({
    loading: true,
    result: null,
  });
  useEffect(() => {
    if (detailsSteps[activeStep].runValidation && data) {
      void (async () => {
        setValidationState({ loading: true, result: null });
        const errors: ValidationError[] = [];
        const sectionsWithErrors: Set<string> = new Set();
        for (const step of detailsSteps) {
          if (step.validator) {
            const stepErrors = await step.validator({
              tenant,
              subaccount,
              campaign: data.getCampaignByShortId,
              subaccountLink,
            });
            if (stepErrors.length) {
              errors.push(
                ...stepErrors.map(error => ({
                  ...error,
                  pageLink: subaccountLink(
                    `/campaigns/${campaignShortId}/edit/${step.slug}`
                  ),
                  pageName: step.name,
                }))
              );
              sectionsWithErrors.add(step.slug);
            }
          }
        }
        setValidationState({
          loading: false,
          result: { errors, sectionsWithErrors },
        });
      })();
    } else {
      setValidationState({
        loading: false,
        result: { errors: [], sectionsWithErrors: new Set() },
      });
    }
  }, [
    detailsSteps,
    activeStep,
    data,
    tenant,
    subaccount,
    subaccountLink,
    campaignShortId,
    setValidationState,
  ]);

  function iconForStep({
    jobInProgress,
    hasError,
  }: {
    jobInProgress: boolean;
    hasError: boolean;
  }) {
    if (jobInProgress) {
      return (
        <Loading
          size={20}
          style={{ margin: "2px" }}
          progressProps={{ thickness: 5 }}
        />
      );
    }

    if (hasError) {
      return <ErrorOutlineRounded color="error" />;
    }

    return null;
  }

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  return (
    <PageContainer>
      <FlexBox direction="column" sx={{ maxWidth: 1028 }}>
        <TopActionButtons campaign={data!.getCampaignByShortId} />

        <AsyncWrapper
          loading={validationState.loading ?? false}
          data={validationState.result}
          errors={null}
        >
          {validationResult => (
            <FlexBox justifyContent="space-between">
              <Box sx={{ width: "188px" }}>
                <Stepper
                  nonLinear
                  activeStep={activeStep}
                  orientation="vertical"
                >
                  {detailsSteps
                    .filter(({ hidden }) => !hidden)
                    .map(({ name, slug, job }, index) => (
                      <Step key={name}>
                        <StepButton
                          onClick={() => goToStep(index)}
                          icon={iconForStep({
                            jobInProgress: !!job && jobInProgress(job),
                            hasError:
                              validationResult?.sectionsWithErrors.has(slug) ??
                              false,
                          })}
                        >
                          <StepLabel
                            error={validationResult?.sectionsWithErrors.has(
                              slug
                            )}
                          >
                            {name}
                          </StepLabel>
                        </StepButton>
                        <StepContent />
                      </Step>
                    ))}
                </Stepper>
              </Box>
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    maxWidth: "840px",
                    backgroundColor: palette.white,
                    borderRadius: 0.5,
                    boxShadow: "0px 1px 5px 0px rgba(106, 103, 131, 0.20)",
                    px: "56px",
                    py: "32px",
                  }}
                >
                  <CurrentPage
                    campaign={data!.getCampaignByShortId}
                    goToNextSection={goToNextSection}
                    validationErrors={validationResult?.errors ?? []}
                    refreshCampaign={refetch}
                  />
                </Box>
              </Box>
            </FlexBox>
          )}
        </AsyncWrapper>
      </FlexBox>
    </PageContainer>
  );
}
